import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useNavigationType, Link } from 'react-router-dom';
import { salesOrdersAPI, OrdersQueryParams } from '../services/api';
import { MobileCard } from '../components/MobileCard';
import InvoiceCreationModal from '../components/InvoiceCreationModal';
import { OrderNeedingInvoice, getOrdersNeedingInvoice } from '../services/quickbooksService';
import { useDebounce } from '../hooks/useDebounce';
import '../components/MobileCard.css';
import './Orders.css';

interface OrderItem {
  id: number;
  formula_name: string;
  is_pellet: boolean;
  total_quantity_lbs: number;
  number_of_packages: number;
  package_type?: string;
  unit_price: number;
  total_price: number;
  price_per_package?: number | null;
  pricing_display_method?: string;
}

interface Order {
  id: number;
  order_number: string;
  customer_name: string;
  customer_id?: number;
  customer_email?: string;
  customer_address?: string;
  customer_city?: string;
  customer_state?: string;
  customer_zip?: string;
  order_date: string;
  delivery_date: string;
  status: string;
  shipping_method: string;
  is_rush: boolean;
  is_tax_exempt: boolean;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  special_instructions: string;
  items: OrderItem[];
  entered_by_name?: string;
  invoice_id?: number;
  invoice_number?: string;
  invoice_status?: string;
}

interface TabCounts {
  needs_attention: number;
  in_progress: number;
  shipped: number;
  delivered: number;
  drafts: number;
  total: number;
  pending: number;
  ready_to_ship: number;
}

interface PaginationInfo {
  count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
}

type TabType = 'needs_attention' | 'in_progress' | 'shipped' | 'delivered' | 'drafts' | 'all';

const TAB_CONFIG: { id: TabType; label: string; countKey: keyof TabCounts }[] = [
  { id: 'needs_attention', label: 'Needs Attention', countKey: 'needs_attention' },
  { id: 'in_progress', label: 'In Progress', countKey: 'in_progress' },
  { id: 'shipped', label: 'Shipped', countKey: 'shipped' },
  { id: 'delivered', label: 'Delivered', countKey: 'delivered' },
  { id: 'drafts', label: 'Drafts', countKey: 'drafts' },
  { id: 'all', label: 'All Orders', countKey: 'total' },
];

const SCROLL_KEY = 'ordersListScrollY';

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const navType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab and pagination state
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const tabParam = searchParams.get('tab');
    return (tabParam as TabType) || 'needs_attention';
  });
  const [currentPage, setCurrentPage] = useState(() => Number(searchParams.get('page')) || 1);
  const [pageSize] = useState(50);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [tabCounts, setTabCounts] = useState<TabCounts | null>(null);

  // Filter state. Customer search uses a debounced pattern: the input
  // updates on every keystroke (responsive UI) but the network-layer
  // `customerSearch` only changes once the user stops typing for 300 ms.
  // Pressing Enter bypasses the debounce.
  // Filter state seeded from the URL so refresh + back-navigation restore it.
  const [showFilters, setShowFilters] = useState(false);
  const [customerSearchInput, setCustomerSearchInput] = useState(() => searchParams.get('q') || '');
  const debouncedCustomerSearch = useDebounce(customerSearchInput, 300);
  const [customerSearch, setCustomerSearch] = useState(() => searchParams.get('q') || '');
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('from') || '');
  const [dateTo, setDateTo] = useState(() => searchParams.get('to') || '');
  const [includeArchived, setIncludeArchived] = useState(() => searchParams.get('archived') === '1');

  // Orders state
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [confirmingOrder, setConfirmingOrder] = useState(false);
  const [orderForInvoice, setOrderForInvoice] = useState<OrderNeedingInvoice | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showInvoicePrompt, setShowInvoicePrompt] = useState(false);
  const [justConfirmedOrder, setJustConfirmedOrder] = useState<Order | null>(null);

  // Bulk selection state
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  // Bulk "Mark Delivered" sweep modal
  const [showDeliverModal, setShowDeliverModal] = useState(false);
  const [deliverNotify, setDeliverNotify] = useState(false);   // notify customer — OFF by default for sweeps

  // Load tab counts
  const loadTabCounts = useCallback(async () => {
    try {
      const response = await salesOrdersAPI.getTabCounts();
      setTabCounts(response.data);
    } catch (err) {
      console.error('Error loading tab counts:', err);
    }
  }, []);

  // Load orders with current filters
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params: OrdersQueryParams = {
        tab: activeTab,
        page: currentPage,
        page_size: pageSize,
      };

      if (customerSearch) params.customer_search = customerSearch;
      if (dateFrom) params.delivery_after = dateFrom;
      if (dateTo) params.delivery_before = dateTo;
      if (includeArchived && (activeTab === 'shipped' || activeTab === 'delivered')) params.include_archived = true;

      const response = await salesOrdersAPI.getAll(params);

      // Handle paginated response
      if (response.data.results) {
        setOrders(response.data.results);
        setPagination({
          count: response.data.count,
          total_pages: response.data.total_pages,
          current_page: response.data.current_page,
          page_size: response.data.page_size,
        });
      } else {
        // Non-paginated response (backwards compat)
        setOrders(response.data);
        setPagination(null);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, pageSize, customerSearch, dateFrom, dateTo, includeArchived]);

  // Initial load
  useEffect(() => {
    loadTabCounts();
  }, [loadTabCounts]);

  // Propagate debounced customer search to the network-layer state. Page resets to 1
  // when the user CHANGES the search — but NOT on the initial mount, so a page number
  // restored from the URL (back-navigation / refresh) survives.
  const searchMountRef = useRef(false);
  useEffect(() => {
    setCustomerSearch(debouncedCustomerSearch);
    if (searchMountRef.current) {
      setCurrentPage(1);
    } else {
      searchMountRef.current = true;
    }
  }, [debouncedCustomerSearch]);

  // Load orders when filters change
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Mirror ALL list filters into the URL so refresh + back-navigation restore them.
  // Keyed on the DEBOUNCED search (customerSearch), so the URL updates once per pause,
  // not per keystroke. `replace: true` means filtering doesn't stack a history entry —
  // browser-back goes to the previous page, not through each filter/tab change.
  useEffect(() => {
    const next = new URLSearchParams();
    next.set('tab', activeTab);
    if (currentPage > 1) next.set('page', String(currentPage));
    if (customerSearch) next.set('q', customerSearch);
    if (dateFrom) next.set('from', dateFrom);
    if (dateTo) next.set('to', dateTo);
    if (includeArchived) next.set('archived', '1');
    setSearchParams(next, { replace: true });
  }, [activeTab, currentPage, customerSearch, dateFrom, dateTo, includeArchived, setSearchParams]);

  // Navigate into an order, saving the list's scroll position first so we can restore
  // it when the user comes back. Use this for every row/button that opens an order.
  const goToOrder = useCallback((id: number) => {
    try { sessionStorage.setItem(SCROLL_KEY, String(window.scrollY)); } catch { /* ignore */ }
    navigate(`/orders/${id}`);
  }, [navigate]);

  // Capture the navigation type AT MOUNT. Our URL-sync effect above calls
  // setSearchParams(replace) on mount, which flips useNavigationType() to 'REPLACE' —
  // so reading it later in the restore would wrongly conclude this wasn't a back/forward
  // (POP) navigation and skip the restore. The ref freezes the value from the first
  // render, before that setSearchParams runs.
  const arrivedViaPop = useRef(navType === 'POP').current;

  // Restore the saved scroll position on BACK-navigation to this list, AFTER the rows
  // have actually rendered (keyed on `loading` going false, i.e. the fetch resolved and
  // setOrders committed — the document now has the height to scroll into). The rAF runs
  // after the app's <ScrollToTop> mount rAF, so our restore wins the scroll.
  const didRestoreScroll = useRef(false);
  useEffect(() => {
    if (loading || didRestoreScroll.current) return;
    didRestoreScroll.current = true;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    sessionStorage.removeItem(SCROLL_KEY);
    if (arrivedViaPop && saved) {
      requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10)));
    }
  }, [loading, arrivedViaPop]);

  // Reset page when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedOrderIds(new Set());
  };

  // Apply filters
  const handleApplyFilters = () => {
    setCurrentPage(1);
    loadOrders();
  };

  // Clear filters
  const handleClearFilters = () => {
    setCustomerSearch('');
    setDateFrom('');
    setDateTo('');
    setIncludeArchived(false);
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusBadgeClass = (status: string) => {
    const statusMap: Record<string, string> = {
      PENDING: 'status-pending',
      CONFIRMED: 'status-confirmed',
      IN_PRODUCTION: 'status-production',
      PRODUCTION: 'status-production',
      MILLED: 'status-production',
      PACKAGING: 'status-production',
      READY: 'status-ready',
      READY_TO_SHIP: 'status-ready',
      SHIPPED: 'status-shipped',
      DELIVERED: 'status-delivered',
      CANCELLED: 'status-cancelled',
      DRAFT: 'status-draft',
    };
    return statusMap[status] || 'status-default';
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatItemPricing = (item: OrderItem) => {
    // Determine if this is a bagged order (40lb or 19lb bags)
    const isBaggedOrder = item.package_type === 'BAGS' || item.package_type === 'BAGS_19';

    // For bagged orders, show price per bag
    if (isBaggedOrder && item.number_of_packages > 0) {
      const pricePerBag = item.total_price / item.number_of_packages;
      return `${formatCurrency(pricePerBag)}/bag × ${item.number_of_packages} bags = ${formatCurrency(item.total_price)}`;
    }

    // For super sacks, bulk, and BYOC - show price per pound
    const pricePerLb = item.total_quantity_lbs > 0 ? item.total_price / item.total_quantity_lbs : 0;
    return `$${pricePerLb.toFixed(4)}/lb × ${item.total_quantity_lbs.toLocaleString()} lbs = ${formatCurrency(item.total_price)}`;
  };

  const handleConfirmOrder = async (orderId: number) => {
    if (!window.confirm('Confirm this order? This will change the status from PENDING to CONFIRMED.')) {
      return;
    }

    try {
      setConfirmingOrder(true);
      await salesOrdersAPI.confirmOrder(orderId);

      // Reload orders and counts
      await Promise.all([loadOrders(), loadTabCounts()]);

      const confirmedOrder = orders.find(o => o.id === orderId);
      if (confirmedOrder) {
        const updatedOrder = { ...confirmedOrder, status: 'CONFIRMED' };
        setJustConfirmedOrder(updatedOrder);
        setShowInvoicePrompt(true);
      }

      setSelectedOrder(null);
      setSuccessMessage('Order confirmed successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error confirming order:', err);
      alert(err.response?.data?.error || 'Failed to confirm order');
    } finally {
      setConfirmingOrder(false);
    }
  };

  const handleInvoicePromptCreate = () => {
    if (justConfirmedOrder) {
      handleCreateInvoiceClick(justConfirmedOrder);
    }
    setShowInvoicePrompt(false);
    setJustConfirmedOrder(null);
  };

  const handleInvoicePromptSkip = () => {
    setShowInvoicePrompt(false);
    setJustConfirmedOrder(null);
  };

  const getInvoiceStatusBadge = (order: Order) => {
    if (order.invoice_status && order.invoice_id) {
      const colorMap: Record<string, string> = {
        'DRAFT': '#6b7280',
        'PENDING_REVIEW': '#f59e0b',
        'APPROVED': '#3b82f6',
        'SENT': '#8b5cf6',
        'PAID': '#22c55e',
        'PARTIALLY_PAID': '#06b6d4',
        'OVERDUE': '#ef4444',
        'VOIDED': '#9ca3af',
      };
      const color = colorMap[order.invoice_status] || '#6b7280';
      return (
        <Link
          to={`/sales/invoices/${order.invoice_id}`}
          className="invoice-badge invoice-link"
          style={{ backgroundColor: `${color}20`, color }}
          onClick={(e) => e.stopPropagation()}
        >
          {order.invoice_number || 'Invoice'}
        </Link>
      );
    }
    return <span className="invoice-badge invoice-none">—</span>;
  };

  const handleCreateInvoiceClick = async (order: Order) => {
    const invoiceOrder: OrderNeedingInvoice = {
      id: order.id,
      order_number: order.order_number,
      customer_id: order.customer_id || 0,
      customer_name: order.customer_name,
      customer_email: order.customer_email || '',
      customer_address: order.customer_address || '',
      customer_city: order.customer_city || '',
      customer_state: order.customer_state || '',
      customer_zip: order.customer_zip || '',
      customer_tax_exempt: order.is_tax_exempt,
      order_date: order.order_date,
      delivery_date: order.delivery_date,
      total: order.total_amount.toString(),
      status: order.status.toLowerCase(),
      shipping_method: order.shipping_method,
      notes: order.special_instructions || '',
      items: order.items.map(item => ({
        id: item.id,
        feed_name: item.formula_name,
        quantity_lbs: item.total_quantity_lbs,
        unit_price: (item.total_price / item.total_quantity_lbs).toFixed(4),
        line_total: item.total_price.toString(),
        package_type: item.package_type || '',
        is_pelleted: item.is_pellet,
        // Bag pricing fields
        number_of_packages: item.number_of_packages || 1,
        price_per_package: item.price_per_package ? String(item.price_per_package) : null,
        pricing_display_method: (item.pricing_display_method as 'PER_LB' | 'PER_PACKAGE') || 'PER_LB',
      })),
    };
    setOrderForInvoice(invoiceOrder);
  };

  const handleInvoiceCreated = (invoiceNumber: string, sent: boolean) => {
    setOrderForInvoice(null);
    setSelectedOrder(null);
    setSuccessMessage(
      sent
        ? `Invoice ${invoiceNumber} created and sent to customer!`
        : `Invoice ${invoiceNumber} saved as draft.`
    );
    loadOrders();
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  // Bulk selection handlers
  const toggleOrderSelection = (orderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOrderIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrderIds.size === orders.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(orders.map(o => o.id)));
    }
  };

  const handleBulkConfirm = async () => {
    const pendingIds = Array.from(selectedOrderIds).filter(id => {
      const order = orders.find(o => o.id === id);
      return order?.status === 'PENDING';
    });

    if (pendingIds.length === 0) {
      setError('No PENDING orders selected for confirmation');
      return;
    }

    if (!window.confirm(`Confirm ${pendingIds.length} order(s)?`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await salesOrdersAPI.bulkConfirm(pendingIds);
      await Promise.all([loadOrders(), loadTabCounts()]);
      setSelectedOrderIds(new Set());
      setSuccessMessage(`${response.data.confirmed.length} order(s) confirmed`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to confirm orders');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    const selectedIds = Array.from(selectedOrderIds);
    if (selectedIds.length === 0) {
      setError('No orders selected');
      return;
    }

    const selectedOrders = selectedIds.map(id => orders.find(o => o.id === id)).filter(Boolean) as Order[];
    const productionStatuses = ['IN_PRODUCTION', 'READY_TO_SHIP', 'SHIPPED'];
    const hasProductionOrders = selectedOrders.some(o => productionStatuses.includes(o.status));

    let message = `You are about to delete ${selectedIds.length} order(s). This will also remove associated batches, allocations, and fulfillments. This cannot be undone.\n\n`;
    message += selectedOrders.map(o => o.order_number).join(', ');

    if (hasProductionOrders) {
      const prodOrders = selectedOrders.filter(o => productionStatuses.includes(o.status));
      message += `\n\nWARNING: ${prodOrders.map(o => o.order_number).join(', ')} ha${prodOrders.length > 1 ? 've' : 's'} active production. Deleting will affect in-progress batches.`;
    }

    message += '\n\nType DELETE to confirm:';
    const confirmation = window.prompt(message);
    if (confirmation !== 'DELETE') {
      if (confirmation !== null) setError('Deletion cancelled — you must type DELETE to confirm');
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await salesOrdersAPI.bulkDelete(selectedIds);
      await Promise.all([loadOrders(), loadTabCounts()]);
      setSelectedOrderIds(new Set());
      setSuccessMessage(`${response.data.deleted.length} order(s) deleted`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete orders');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk "Mark Delivered" sweep — silent by default (notify OFF). Reuses the same
  // server-side DELIVERED path as single-order update_status.
  const confirmBulkMarkDelivered = async () => {
    const selectedIds = Array.from(selectedOrderIds);
    if (selectedIds.length === 0) return;
    try {
      setBulkActionLoading(true);
      const response = await salesOrdersAPI.bulkMarkDelivered(selectedIds, deliverNotify);
      await Promise.all([loadOrders(), loadTabCounts()]);
      setSelectedOrderIds(new Set());
      setShowDeliverModal(false);
      const { delivered, emails_sent, skipped } = response.data;
      setSuccessMessage(
        `Marked ${delivered} delivered. ${emails_sent} customer email(s) sent. ${skipped.length} skipped.`
      );
      setTimeout(() => setSuccessMessage(null), 6000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to mark orders delivered');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Pagination controls
  const goToPage = (page: number) => {
    if (page >= 1 && pagination && page <= pagination.total_pages) {
      setCurrentPage(page);
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="orders-page">
        <div className="loading">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="page-header">
        <h1>Sales Orders</h1>
        <p className="page-subtitle">Red Bridge Farm Feed Co.</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      {/* Tabs */}
      <div className="orders-tabs">
        {TAB_CONFIG.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
            {tabCounts && tabCounts[tab.countKey] > 0 && (
              <span className={`tab-badge ${tab.id === 'needs_attention' ? 'badge-warning' : ''}`}>
                {tabCounts[tab.countKey]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="orders-container">
        {/* Filters and Actions Bar */}
        <div className="filters-section">
          <div className="filter-row">
            <button
              className={`btn-filter-toggle ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? '▼ Filters' : '▶ Filters'}
            </button>

            {pagination && (
              <div className="results-count">
                Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, pagination.count)} of {pagination.count} orders
              </div>
            )}

            <button className="btn-new-order" onClick={() => navigate('/order-entry')}>
              + New Order
            </button>
          </div>

          {/* Collapsible Filters */}
          {showFilters && (
            <div className="filters-panel">
              <div className="filter-group">
                <label>Customer Search</label>
                <input
                  type="text"
                  placeholder="Search by customer name..."
                  value={customerSearchInput}
                  onChange={(e) => setCustomerSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setCustomerSearch(customerSearchInput);
                      setCurrentPage(1);
                    }
                  }}
                  className="filter-input"
                />
              </div>
              <div className="filter-group">
                <label>Delivery From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="filter-input"
                />
              </div>
              <div className="filter-group">
                <label>Delivery To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="filter-input"
                />
              </div>
              {activeTab === 'shipped' && (
                <div className="filter-group filter-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={includeArchived}
                      onChange={(e) => setIncludeArchived(e.target.checked)}
                    />
                    Include orders older than 30 days
                  </label>
                </div>
              )}
              <div className="filter-actions">
                <button className="btn btn-primary" onClick={handleApplyFilters}>
                  Apply Filters
                </button>
                <button className="btn btn-secondary" onClick={handleClearFilters}>
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Action Bar */}
        {selectedOrderIds.size > 0 && (
          <div className="bulk-action-bar">
            <span className="bulk-count">{selectedOrderIds.size} order(s) selected</span>
            <div className="bulk-actions">
              <button
                className="btn btn-bulk-confirm"
                onClick={handleBulkConfirm}
                disabled={bulkActionLoading}
              >
                ✅ Confirm Selected
              </button>
              <button
                className="btn btn-bulk-confirm"
                onClick={() => { setDeliverNotify(false); setShowDeliverModal(true); }}
                disabled={bulkActionLoading}
              >
                📦 Mark Delivered ({selectedOrderIds.size})
              </button>
              <button
                className="btn btn-bulk-delete"
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
              >
                🗑️ Delete Selected
              </button>
              <button
                className="btn btn-bulk-clear"
                onClick={() => setSelectedOrderIds(new Set())}
                disabled={bulkActionLoading}
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Loading indicator for subsequent loads */}
        {loading && orders.length > 0 && (
          <div className="loading-overlay">Loading...</div>
        )}

        {/* Orders Table */}
        <div className="desktop-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={orders.length > 0 && selectedOrderIds.size === orders.length}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                <th>Order #</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Delivery Date</th>
                <th>Entered By</th>
                <th>Total</th>
                <th>Invoice</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={10} className="no-data">
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className={`order-row ${selectedOrderIds.has(order.id) ? 'row-selected' : ''}`} onClick={() => goToOrder(order.id)}>
                    <td className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={selectedOrderIds.has(order.id)}
                        onChange={() => {}}
                        onClick={(e) => toggleOrderSelection(order.id, e)}
                      />
                    </td>
                    <td className="order-number">{order.order_number}</td>
                    <td>{formatDate(order.order_date)}</td>
                    <td className="customer-name">{order.customer_name}</td>
                    <td>{formatDate(order.delivery_date)}</td>
                    <td>{order.entered_by_name || '-'}</td>
                    <td className="order-total">{formatCurrency(order.total_amount)}</td>
                    <td>{getInvoiceStatusBadge(order)}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                        {formatStatus(order.status)}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-view"
                        onClick={(e) => {
                          e.stopPropagation();
                          goToOrder(order.id);
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {pagination && pagination.total_pages > 1 && (
          <div className="pagination-controls">
            <button
              className="btn btn-pagination"
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
            >
              « First
            </button>
            <button
              className="btn btn-pagination"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ‹ Prev
            </button>

            <span className="pagination-info">
              Page {currentPage} of {pagination.total_pages}
            </span>

            <button
              className="btn btn-pagination"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === pagination.total_pages}
            >
              Next ›
            </button>
            <button
              className="btn btn-pagination"
              onClick={() => goToPage(pagination.total_pages)}
              disabled={currentPage === pagination.total_pages}
            >
              Last »
            </button>
          </div>
        )}

        {/* Mobile Cards (shown on small screens) */}
        <div className="mobile-cards-container">
          <div className="mobile-card-list">
            {orders.length === 0 ? (
              <div className="no-data-card">No orders found</div>
            ) : (
              orders.map((order) => (
                <MobileCard
                  key={order.id}
                  title={order.order_number}
                  subtitle={order.customer_name}
                  badge={
                    <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                      {formatStatus(order.status)}
                    </span>
                  }
                  fields={[
                    { label: 'Order Date', value: formatDate(order.order_date) },
                    { label: 'Delivery', value: formatDate(order.delivery_date) },
                    { label: 'Total', value: formatCurrency(order.total_amount), className: 'highlight' },
                    { label: 'Entered By', value: order.entered_by_name || '-' },
                  ]}
                  actions={
                    <button className="btn btn-primary btn-sm" onClick={() => goToOrder(order.id)}>
                      View Details
                    </button>
                  }
                  onClick={() => goToOrder(order.id)}
                  className={order.status === 'PENDING' ? 'status-pending' : ''}
                />
              ))
            )}
          </div>

          {/* Mobile Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="pagination-controls mobile">
              <button
                className="btn btn-pagination"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ‹ Prev
              </button>
              <span className="pagination-info">
                {currentPage} / {pagination.total_pages}
              </span>
              <button
                className="btn btn-pagination"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === pagination.total_pages}
              >
                Next ›
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="modal-overlay order-detail-modal" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content order-detail-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Order {selectedOrder.order_number}</h2>
                <span className={`status-badge ${getStatusBadgeClass(selectedOrder.status)}`}>
                  {formatStatus(selectedOrder.status)}
                </span>
              </div>
              <button className="modal-close" onClick={() => setSelectedOrder(null)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Order Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Customer:</label>
                    <span>{selectedOrder.customer_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Order Date:</label>
                    <span>{formatDate(selectedOrder.order_date)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Delivery Date:</label>
                    <span>{formatDate(selectedOrder.delivery_date)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Shipping Method:</label>
                    <span>{selectedOrder.shipping_method.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="detail-item">
                    <label>Entered By:</label>
                    <span>{selectedOrder.entered_by_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Rush Order:</label>
                    <span>{selectedOrder.is_rush ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Tax Exempt:</label>
                    <span>{selectedOrder.is_tax_exempt ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Order Items</h3>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Feed Formula</th>
                      <th>Form</th>
                      <th>Quantity (lbs)</th>
                      <th>Packages</th>
                      <th>Pricing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items && selectedOrder.items.length > 0 ? (
                      selectedOrder.items.map((item) => (
                        <tr key={item.id}>
                          <td>{item.formula_name}</td>
                          <td>{item.is_pellet ? 'Pellet' : 'Mash'}</td>
                          <td>{item.total_quantity_lbs.toLocaleString()}</td>
                          <td>{item.number_of_packages}</td>
                          <td>{formatItemPricing(item)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="no-data">
                          No items
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="detail-section">
                <h3>Order Total</h3>
                <div className="total-breakdown">
                  <div className="total-row">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(selectedOrder.subtotal)}</span>
                  </div>
                  <div className="total-row">
                    <span>Tax:</span>
                    <span>{formatCurrency(selectedOrder.tax_amount)}</span>
                  </div>
                  <div className="total-row total-final">
                    <span>Total:</span>
                    <span>{formatCurrency(selectedOrder.total_amount)}</span>
                  </div>
                </div>
              </div>

              {selectedOrder.special_instructions && (
                <div className="detail-section">
                  <h3>Special Instructions</h3>
                  <div className="notes-block">{selectedOrder.special_instructions}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {selectedOrder.status === 'PENDING' && (
                <button
                  className="btn btn-confirm no-print"
                  onClick={() => handleConfirmOrder(selectedOrder.id)}
                  disabled={confirmingOrder}
                >
                  ✅ {confirmingOrder ? 'Confirming...' : 'Confirm Order'}
                </button>
              )}
              {(selectedOrder.status === 'PENDING' || selectedOrder.status === 'CONFIRMED' || selectedOrder.status === 'DRAFT') && (
                <button
                  className="btn btn-edit no-print"
                  onClick={() => navigate(`/order-entry?edit=${selectedOrder.id}`)}
                >
                  ✏️ Edit Order
                </button>
              )}
              <button
                className="btn btn-cancel-order no-print"
                onClick={async () => {
                  const productionStatuses = ['IN_PRODUCTION', 'READY_TO_SHIP', 'SHIPPED'];
                  const isProduction = productionStatuses.includes(selectedOrder.status);
                  let message = `You are about to delete Order #${selectedOrder.order_number}. This will also remove associated batches, allocations, and fulfillments. This cannot be undone.`;
                  if (isProduction) {
                    message += `\n\nWARNING: This order has active production. Deleting will affect in-progress batches.`;
                  }
                  message += '\n\nType DELETE to confirm:';
                  const confirmation = window.prompt(message);
                  if (confirmation !== 'DELETE') {
                    if (confirmation !== null) setError('Deletion cancelled — you must type DELETE to confirm');
                    return;
                  }
                  try {
                    await salesOrdersAPI.delete(selectedOrder.id);
                    setSelectedOrder(null);
                    await Promise.all([loadOrders(), loadTabCounts()]);
                    setSuccessMessage(`Order #${selectedOrder.order_number} deleted successfully`);
                    setTimeout(() => setSuccessMessage(null), 5000);
                  } catch (err: any) {
                    setError(err.response?.data?.error || 'Failed to delete order');
                  }
                }}
              >
                🗑️ Delete Order
              </button>
              {selectedOrder.status !== 'PENDING' && selectedOrder.status !== 'CANCELLED' && !selectedOrder.invoice_id && (
                <button
                  className="btn btn-invoice no-print"
                  onClick={() => handleCreateInvoiceClick(selectedOrder)}
                >
                  📄 Create Invoice
                </button>
              )}
              {selectedOrder.invoice_id && (
                <button
                  className="btn btn-view-invoice no-print"
                  onClick={() => navigate(`/sales/invoices/${selectedOrder.invoice_id}`)}
                >
                  📄 View Invoice ({selectedOrder.invoice_number})
                </button>
              )}
              <button className="btn btn-primary no-print" onClick={() => window.print()}>
                🖨️ Print Order
              </button>
              <button className="btn btn-secondary no-print" onClick={() => setSelectedOrder(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-Confirmation Invoice Prompt */}
      {showInvoicePrompt && justConfirmedOrder && (
        <div className="modal-overlay" onClick={handleInvoicePromptSkip}>
          <div className="invoice-prompt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invoice-prompt-header">
              <span className="invoice-prompt-icon">✅</span>
              <h3>Order Confirmed!</h3>
            </div>
            <div className="invoice-prompt-body">
              <p>
                <strong>Order #{justConfirmedOrder.order_number}</strong> for{' '}
                <strong>{justConfirmedOrder.customer_name}</strong> has been confirmed.
              </p>
              <p>Would you like to create an invoice now?</p>
            </div>
            <div className="invoice-prompt-footer">
              <button
                className="btn btn-invoice-prompt-create"
                onClick={handleInvoicePromptCreate}
              >
                📄 Create Invoice Now
              </button>
              <button
                className="btn btn-invoice-prompt-skip"
                onClick={handleInvoicePromptSkip}
              >
                Skip for Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Creation Modal */}
      {orderForInvoice && (
        <InvoiceCreationModal
          order={orderForInvoice}
          onClose={() => setOrderForInvoice(null)}
          onSuccess={handleInvoiceCreated}
        />
      )}

      {/* Bulk Mark Delivered confirmation */}
      {showDeliverModal && (
        <div className="modal-overlay" onClick={() => !bulkActionLoading && setShowDeliverModal(false)}>
          <div className="modal-content" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>
              Mark {selectedOrderIds.size} order{selectedOrderIds.size !== 1 ? 's' : ''} delivered?
            </h3>
            <p style={{ color: '#475569', fontSize: 13.5, marginTop: 4 }}>
              Already-delivered or cancelled orders are skipped. Delivered orders drop
              out of the active tabs (they move to the Delivered tab).
            </p>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e5e7eb',
              borderRadius: 6, padding: 8, background: '#f8fafc', margin: '8px 0' }}>
              {Array.from(selectedOrderIds)
                .map(id => orders.find(o => o.id === id))
                .filter(Boolean)
                .map((o) => (
                  <div key={(o as Order).id} style={{ fontSize: 13 }}>
                    <strong>{(o as Order).order_number}</strong> · {(o as Order).customer_name}
                  </div>
                ))}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, margin: '10px 0' }}>
              <input
                type="checkbox"
                checked={deliverNotify}
                onChange={(e) => setDeliverNotify(e.target.checked)}
              />
              Notify customer{deliverNotify ? '' : ' (off — no emails will be sent)'}
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button className="btn btn-secondary" onClick={() => setShowDeliverModal(false)} disabled={bulkActionLoading}>
                Cancel
              </button>
              <button className="btn btn-bulk-confirm" onClick={confirmBulkMarkDelivered} disabled={bulkActionLoading}>
                {bulkActionLoading ? 'Marking…' : `Mark Delivered`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
